import React from 'react';
import { useTranslation } from 'react-i18next';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import LockIcon from '@/assets/icons/lock-md';
import { Remove } from '@/assets/icons/remove';
import { Text } from '@/components/TextComponent';
import { AnimatedPressableOpacity } from '@/components/animated-pressable-opacity/AnimatedPressableOpacity';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerProfileImage } from '@/components/player-profile/PlayerProfileImage';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import {
    allPlayerPropsSelector,
    playerPropsSelector,
    usePlayerPropsStore,
} from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { ProjectionInvalidReason } from '@/feature/betslip-pickem/utils/betslip-projection-changed';
import { PickButtonsWithFunctionality, RowProps } from '@/feature/lobby/components/PickProjectionButtonsSection';
import { PlayerDetails } from '@/feature/lobby/components/PlayerDetails';
import { resources } from '@/i18n/i18n.config';
import { common, withOpacity } from '@/styles/styles';
import { League, MarketStatus } from '@/types/api.generated';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { getPlayerLogoDetails } from '@/utils/get-player-logo-details';
import { useShallow } from 'zustand/react/shallow';

const NO_PROJECTIONS: PlayerProjectionFragment[] = [];

/*
 * This component is responsible for rendering the player row in many place in the app
 * It can contain the player's image, details, projection buttons and remove button
 * Selection Mode: is used when the user has already selected a projection for the player
 * Highlight Mode: is used when  we display the details of a projection, but there is no selection yet, however this mode displays the selection buttons
 * Change Mode is used to display the old and new values of a selected projection(has an existing outcome).
 * All Mode: is used to display the details of all the user's projections and the plus sign to open the player card
 * Display Mode: is used when we don't show any buttons, nor information on projections, so the player type doesn't need to contain all the projections
 * Unavailable Mode: is used when we want to show 'Unavailable' card and player's information is cut
 * Disable Changes Mode: is used when we want to show the player's information and the new projection value without the buttons to change the projection
 * */
export const PlayerRow = (props: RowProps) => {
    const {
        player,
        mode,
        event,
        pressable,
        analyticsTag,
        testID,
        paddingVertical = 's16',
        hideGameTime,
        openPlayerProfile,
    } = props;
    const newProjection = mode === 'change' || mode === 'disable-changes' ? props.newProjection : undefined;
    const outcome = 'outcome' in props ? props.outcome : undefined;
    const team = 'team' in player ? player.team : undefined;
    const allProjections = usePlayerPropsStore(state => {
        if (!event?.id) {
            return NO_PROJECTIONS;
        }
        return allPlayerPropsSelector(player.id, event.id)(state);
    });
    const playerProjections = usePlayerPropsStore(
        useShallow(state => {
            if (mode === 'race-2-place') {
                return props.projections;
            }
            if (!event?.id) {
                return [];
            }
            return playerPropsSelector(player.id, event.id)(state);
        })
    );

    const league = event?.league || team?.league || ('league' in player ? (player.league as League) : undefined);
    const { logoText, teamLogo } = getPlayerLogoDetails(player, league, team);
    const backgroundColorForUnavailable = withOpacity(ds.colorValue.background.danger.strong, 0.1);
    const { t } = useTranslation('lineup_update_modal');
    const { leagueColor, leagueIcon } = useLeagueConfigsStore(useShallow(leagueConfigSelector(league)));

    const singleProjection =
        'projections' in player ? (playerProjections.length === 1 ? playerProjections[0] : undefined) : undefined;
    const selectedProjection = mode !== 'all' ? props.projection : singleProjection;

    const playerNumber = getPlayerJerseyNumber(league, player?.jerseyNumber);

    const playerSuspended =
        allProjections.length > 0 && allProjections.every(p => p.marketStatus === MarketStatus.Suspended);
    const selectionSuspended = selectedProjection?.marketStatus === MarketStatus.Suspended;

    //Show the row as suspended if the player is suspended or the selection is suspended and we are in the pick-slip mode
    //If the selection is suspended, but the player still has active props we don't want to show the row as suspended
    //because the user can still change the selection
    const showAsSuspended = playerSuspended || (selectionSuspended && mode === 'pick-slip');

    //We always want to show the delete button when rendering the row in the pick-slip
    //to let the user remove the player from the slip if it's suspended
    const showTrashIfSuspended = mode === 'pick-slip' && props.showTrash;

    const disabled = !pressable;

    const hideBorder = props.hideBorder ?? (mode === 'pick-slip' || mode === 'race-2-place');
    const showButtons = mode !== 'disable-changes' && mode !== 'pick-slip' && mode !== 'race-2-place';

    const openPlayerCard = () => {
        if (openPlayerProfile && event?.id) {
            openPlayerProfile(player.id, event.id);

            if (analyticsTag) {
                BetrAnalytics.trackEvent(AnalyticsEvent.EXPAND_PLAYER_PROPS, {
                    location: analyticsTag,
                });
            }
        }
    };

    return (
        <Box
            flexDirection={'row'}
            alignItems={'center'}
            paddingVertical={paddingVertical}
            testID={`playerRow-${testID}`}
        >
            {props.showTrash ? <RemoveFromLineupIcon onPress={props.onRemoveFromLineup} /> : null}
            <AnimatedPressableOpacity
                onPress={openPlayerCard}
                disabled={disabled}
                flexDirection={'row'}
                activeOpacity={disabled ? 1 : 0.7}
                flex={1}
                accessible={false}
            >
                <Row alignItems={'center'} flex={1}>
                    <Box
                        mr={'s16'}
                        opacity={showAsSuspended ? 0.5 : 1}
                        // the opacity is not applied to all children on android
                        // so we need to set these props to make sure the opacity is applied to the children
                        // and also enable them only when the row is suspended as they
                        // are heavy on the memory
                        needsOffscreenAlphaCompositing={showAsSuspended}
                        renderToHardwareTextureAndroid={showAsSuspended}
                    >
                        <PlayerProfileImage
                            // the key is used to force the component to re-render when the player changes
                            // this is needed due to how FlashList recycles the components
                            key={`${player.firstName}-${player.lastName}-${player.id}`}
                            logoText={logoText}
                            playerImageUrl={player.icon}
                            teamImageUrl={teamLogo ?? leagueIcon}
                            teamColor={team?.color ?? leagueColor}
                            teamSecondaryColor={team?.secondaryColor}
                            bgColor={
                                props.presentation === 'sheet'
                                    ? ds.colorToken.background.neutral.subtle
                                    : ds.colorToken.background.neutral.subtler
                            }
                            playerNumber={playerNumber}
                            playerId={player.id}
                            hideBorder={hideBorder}
                        />
                    </Box>
                    <Box flex={1}>
                        <PlayerDetails
                            isSuspended={showAsSuspended}
                            projections={
                                [
                                    'selection',
                                    'race-2-place',
                                    'pick-slip',
                                    'highlight',
                                    'all',
                                    'change',
                                    'disable-changes',
                                ].includes(mode)
                                    ? playerProjections
                                    : []
                            }
                            player={player}
                            event={event}
                            isVoided={mode === 'unavailable'}
                            teamName={team?.name || ''}
                            selectedProjection={selectedProjection}
                            newProjection={newProjection}
                            testID={`playerEventDetails-${testID}`}
                            hideGameTime={hideGameTime}
                            outcome={outcome}
                        />
                    </Box>
                </Row>
            </AnimatedPressableOpacity>
            {!showAsSuspended ? (
                <Row ml={'s16'}>
                    {mode === 'unavailable' ? (
                        <Box
                            px={'s6'}
                            style={[{ backgroundColor: backgroundColorForUnavailable }, common.borderCurve]}
                            borderRadius={'r6'}
                        >
                            <Text variant={'bodySmall'} color={ds.colorToken.text.danger}>
                                {t(mapReasonToI18NKey(props.reason))}
                            </Text>
                        </Box>
                    ) : showButtons ? (
                        <PickButtonsWithFunctionality
                            testID={testID}
                            {...props}
                            leagueIcon={leagueIcon}
                            leagueColor={leagueColor}
                            selectedProjection={selectedProjection}
                        />
                    ) : null}
                </Row>
            ) : (
                <Row ml={'s16'} p={'s12'} pr={showTrashIfSuspended ? 's0' : 's12'}>
                    <LockIcon />
                </Row>
            )}
        </Box>
    );
};

const RemoveFromLineupIcon = ({ onPress }: { onPress: () => void }) => {
    return (
        <TouchableBox
            onPress={() => {
                BetrAnalytics.trackEvent(AnalyticsEvent.CLICK, {
                    button: 'Remove from lineup',
                });
                onPress();
            }}
            hitSlop={8}
            testID="removeFromLineupIcon"
            mr={'s16'}
        >
            <Remove />
        </TouchableBox>
    );
};

type InvalidLineupKey = keyof (typeof resources)['en']['lineup_update_modal'];
function mapReasonToI18NKey(reason: ProjectionInvalidReason): InvalidLineupKey {
    switch (reason) {
        case 'event-removed':
            return 'projection_event_removed';
        case 'removed':
            return 'projection_removed';
        case 'type-changed':
            return 'projection_type_changed';
        case 'outcome-removed':
            return 'projection_outcome_removed';
        case 'suspended':
            return 'projection_suspended';
        default:
            return 'unavailable';
    }
}

export const PLAYER_ROW_HEIGHT = 100;
